import React from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Paper,
  Box,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { FaEdit } from 'react-icons/fa';
import { LoadingButton } from '@mui/lab';
import { Link } from "react-router-dom";
import { formatCurrencyBRL } from "../../utils/formatCurrencyBRL";

const SummaryStep = ({
  selectedAddress,
  selectedPayment,
  selectedDispatch,
  addressList,
  prodListCart,
  grandTotal,
  vlMinOrder,
  errors,
  control,
  Controller,
  isSending,
  onBack,
  onEditAddress,
  onEditPayment,
  onEditShipping,
  onSubmit,
  findColorById,
  findSizeById,
  calculateTotalPrice,
  calculateTotalQuantity
}) => {
  return (
    <div>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Resumo do Pedido
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Endereço */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              <Typography variant="h6" color="#000">
                Endereço de Entrega
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<FaEdit />}
                onClick={onEditAddress}
                style={{
                  fontWeight: 'bold',
                  textTransform: 'none',
                  padding: '8px 16px',
                  backgroundColor: 'black'
                }}
              >
                Alterar
              </Button>
            </Box>
            {selectedAddress && addressList
              .filter((address) => address.ID_DM002 === selectedAddress)
              .map((address) => (
                <div key={address.ID_DM002}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{address.NM_ENDERECO}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {address.DS_LOGRADOURO}, {address.NR_PREDIO}
                    {address.DS_COMPLEMENTO && ` (${address.DS_COMPLEMENTO})`}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {address.NM_BAIRRO}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {address.NM_CIDADE} - {address.NM_UF}
                  </Typography>
                  <Typography variant="body2">
                    CEP: {address.NR_CEP}
                  </Typography>
                </div>
              ))}
          </Paper>
        </Grid>

        {/* Pagamento */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              <Typography variant="h6" color="#000">
                Forma de Pagamento
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<FaEdit />}
                onClick={onEditPayment}
                style={{
                  fontWeight: 'bold',
                  textTransform: 'none',
                  padding: '8px 16px',
                  backgroundColor: 'black'
                }}
              >
                Alterar
              </Button>
            </Box>
            <Typography variant="body1">
              {selectedPayment}
            </Typography>
          </Paper>
        </Grid>

        {/* Envio */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              <Typography variant="h6" color="#000">
                Forma de Envio
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<FaEdit />}
                onClick={onEditShipping}
                style={{
                  fontWeight: 'bold',
                  textTransform: 'none',
                  padding: '8px 16px',
                  backgroundColor: 'black'
                }}
              >
                Alterar
              </Button>
            </Box>
            <Typography variant="body1">
              {selectedDispatch}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Produtos */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Nome do Produto</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Variações</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">Quantidade</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">Preço Unitário</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prodListCart.map((prod, index) => {
              const totalPrice = calculateTotalPrice(prod.quantities, prod.price);
              const totalQuantity = calculateTotalQuantity(prod.quantities);
              return (
                <TableRow key={index}>
                  <TableCell>
                    {prod.name} - Lado: {prod.laterality === 'C' ? 'Canhoto' : prod.laterality === 'D' ? 'Destro' : 'Neutro'}
                  </TableCell>
                  <TableCell>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Cor</TableCell>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">Tamanho</TableCell>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">Quantidade</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(prod.quantities)
                          .map(([key, value]) => {
                            const [colorId, sizeId] = key.split('-');
                            const color = findColorById(colorId);
                            const size = findSizeById(sizeId);
                            return { color, size, colorId, sizeId, value };
                          })
                          .sort((a, b) => a.color.localeCompare(b.color))
                          .map(({ color, size, colorId, sizeId, value }) => (
                            <TableRow key={`${colorId}-${sizeId}`}>
                              <TableCell>{color}</TableCell>
                              <TableCell align="center">{size}</TableCell>
                              <TableCell align="center">{value}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableCell>
                  <TableCell align="center">{totalQuantity}</TableCell>
                  <TableCell align="right">{formatCurrencyBRL(prod.price)}</TableCell>
                  <TableCell align="right" style={{fontWeight:600}}>{formatCurrencyBRL(totalPrice)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Observação */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Observação:
        </Typography>
        <Controller
          name="observation"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              multiline
              rows={4}
              fullWidth
              error={!!errors.observation}
              helperText={errors.observation?.message}
            />
          )}
        />
      </Box>

      {/* Total e Botões */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={12} style={{ textAlign: 'right', fontWeight: 'bold' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            Valor Total: {formatCurrencyBRL(grandTotal)}
          </Typography>
        </Grid>
        <Grid item xs={12} style={{ textAlign: 'right' }}>
          <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} justifyContent="flex-end">
            <Link to="/cart" style={{ textDecoration: 'none' }}>
              <LoadingButton
                variant="outlined"
                color="primary"
                loading={isSending}
                size="large"
                sx={{ 
                  px: 4, 
                  fontWeight: 'bold',
                  borderColor: 'black',
                  color: 'black',
                  '&:hover': {
                    borderColor: 'black',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                Voltar ao Carrinho
              </LoadingButton>
            </Link>
            {vlMinOrder < grandTotal && (
              <LoadingButton
                onClick={onSubmit}
                variant="contained"
                color="success"
                loading={isSending}
                size="large"
                sx={{ px: 4, fontWeight: 'bold' }}
              >
                {isSending ? 'Enviando...' : 'Finalizar Pedido'}
              </LoadingButton>
            )}
          </Stack>
        </Grid>
      </Grid>
    </div>
  );
};

export default SummaryStep;
